import React, { useEffect, useState } from 'react'
import { Alert, Badge, Button, Card } from 'react-bootstrap'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import AdmissionLayout from './AdmissionLayout'
import ApplicantForm from './ApplicantForm'
import { getApplicant, updateApplicant } from './api/admissionApi'
import { rowToForm, CLASS_LABEL, STUDY_LABEL } from './fields'
import { useLoading } from '../contexts/LoadingContext'

// แก้ข้อมูลผู้สมัคร — ใช้ ApplicantForm ตัวเดียวกับหน้ารับสมัคร
// ระบบเดิมแยกเป็น EditMatthayomComponent / EditPrathomComponent / UpdateStudentComponent
// รวมกันเกือบ 3,000 บรรทัดที่เป็นฟอร์มเดียวกัน

export default function AdmissionEdit() {
  const { st_id } = useParams()
  const navigate = useNavigate()
  const { setLoading } = useLoading()

  const [row, setRow] = useState(null)
  const [initial, setInitial] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    getApplicant(st_id)
      .then((data) => {
        if (!alive) return
        setRow(data)
        setInitial(rowToForm(data))
      })
      .catch((err) => alive && setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ'))
      .finally(() => alive && setLoading(false))
    return () => { alive = false }
  }, [st_id, setLoading])

  const handleSubmit = async (payload) => {
    setSubmitting(true)
    setLoading(true)
    try {
      const res = await updateApplicant(st_id, payload)
      await Swal.fire({
        icon: res.changed ? 'success' : 'info',
        title: res.changed ? 'บันทึกแล้ว' : 'ไม่มีข้อมูลที่เปลี่ยนแปลง',
        text: res.changed ? `แก้ไข ${res.changed} รายการ` : 'ข้อมูลเหมือนเดิมทุกช่อง จึงไม่ได้บันทึกอะไร',
        timer: res.changed ? 1600 : undefined,
        showConfirmButton: !res.changed,
      })
      if (res.changed) navigate('/admission/applicants')
    } catch (err) {
      const data = err.response?.data
      Swal.fire({
        icon: 'error',
        title: data?.title || 'บันทึกไม่สำเร็จ',
        text: data?.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่',
      })
    } finally {
      setSubmitting(false)
      setLoading(false)
    }
  }

  return (
    <AdmissionLayout
      title="แก้ไขข้อมูลผู้สมัคร"
      breadcrumb={[{ label: 'รายชื่อผู้สมัคร', to: '/admission/applicants' }, { label: 'แก้ไข' }]}
      actions={row && (
        <Button as={Link} to={`/admission/print/${st_id}`} variant="outline-primary">
          <i className="ri-printer-line me-1" />พิมพ์ใบมอบตัว
        </Button>
      )}
    >
      {error && <Alert variant="danger">{error}</Alert>}

      {row && (
        <Card className="card-one mb-3">
          <Card.Body className="d-flex flex-wrap align-items-center gap-3">
            <div>
              <div className="fs-xs text-secondary">เลขประจำตัว</div>
              <div className="fs-5 fw-semibold">{String(row.st_idnumber || '').trim()}</div>
            </div>
            <div className="border-start ps-3">
              <div className="fs-xs text-secondary">ชื่อ-นามสกุล</div>
              <div className="fw-semibold">{row.st_name}</div>
            </div>
            <div className="border-start ps-3">
              <div className="fs-xs text-secondary">สมัครเข้า</div>
              <div className="fw-semibold">
                {STUDY_LABEL[row.regis_academicStudy] || '-'}
                {' · '}
                {CLASS_LABEL[row.regis_academicClass] || row.regis_academicClass || '-'}
              </div>
            </div>
            <div className="border-start ps-3">
              <div className="fs-xs text-secondary">สถานะ</div>
              <div>
                {row.st_status === '3'
                  ? <Badge bg="warning">ผู้สมัคร (ยังไม่จัดเข้าห้อง)</Badge>
                  : <Badge bg="success">นักเรียนปัจจุบัน</Badge>}
              </div>
            </div>
            <div className="border-start ps-3">
              <div className="fs-xs text-secondary">ผู้รับสมัคร</div>
              <div className="fw-semibold">{row.regis_by || '-'}</div>
            </div>
          </Card.Body>
        </Card>
      )}

      {row && row.st_status !== '3' && (
        <Alert variant="warning">
          <i className="ri-alert-line me-1" />
          รายนี้ถูกจัดเข้าห้องเรียนแล้ว การแก้ไขที่นี่จะกระทบข้อมูลนักเรียนปัจจุบัน
          และถูกบันทึกลงประวัติการแก้ไขข้อมูล
        </Alert>
      )}

      {initial && (
        <ApplicantForm
          mode="edit"
          initialValue={initial}
          submitting={submitting}
          onSubmit={handleSubmit}
          onCancel={() => navigate('/admission/applicants')}
        />
      )}
    </AdmissionLayout>
  )
}
